import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Upload, Clipboard, FileText, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Detects the delimiter used in a CSV/TSV string
 * @param {string} content - The content to analyze
 * @returns {string} - ',' for CSV or '\t' for TSV
 */
const detectDelimiter = (content) => {
  if (!content) return ',';
  const firstLine = content.split(/\r?\n/)[0];
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  return tabCount > commaCount ? '\t' : ',';
};

/**
 * Parses CSV/TSV content and returns preview information
 * @param {string} content - The content to parse
 * @param {string} delimiter - The delimiter to use
 * @returns {object} - Preview info with columns, rows, format, etc.
 */
const parsePreview = (content, delimiter) => {
  if (!content || !content.trim()) {
    return { columns: [], rowCount: 0, format: null, hasAccountColumn: false, sampleAccounts: [] };
  }

  const lines = content.split(/\r?\n/).map(line => line.trim()).filter(line => line);
  if (lines.length === 0) {
    return { columns: [], rowCount: 0, format: null, hasAccountColumn: false, sampleAccounts: [] };
  }

  const header = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
  const dataRows = lines.slice(1);

  // Check for Account column
  const accountIndex = header.findIndex(h => h.toLowerCase() === 'account');
  const hasAccountColumn = accountIndex !== -1;

  // Get sample account numbers (first 5)
  const sampleAccounts = [];
  if (hasAccountColumn) {
    for (let i = 0; i < Math.min(5, dataRows.length); i++) {
      const values = dataRows[i].split(delimiter).map(v => v.trim().replace(/^"|"$/g, ''));
      if (values[accountIndex]) {
        sampleAccounts.push(values[accountIndex]);
      }
    }
  }

  return {
    columns: header,
    rowCount: dataRows.length,
    format: delimiter === '\t' ? 'Tab-separated (Excel)' : 'Comma-separated (CSV)',
    hasAccountColumn,
    sampleAccounts
  };
};

const BudgetImportModal = ({
  isOpen,
  onClose,
  onImport,
  selectedYear,
  isUploading = false
}) => {
  const [activeTab, setActiveTab] = useState('paste'); // 'paste' or 'upload'
  const [pasteContent, setPasteContent] = useState('');
  const [uploadedContent, setUploadedContent] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [error, setError] = useState(null);
  const [delimiter, setDelimiter] = useState(',');

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get the active content based on tab
  const activeContent = activeTab === 'paste' ? pasteContent : uploadedContent;

  // Parse preview info
  const preview = useMemo(() => {
    return parsePreview(activeContent, delimiter);
  }, [activeContent, delimiter]);

  // Focus textarea when modal opens or tab changes to paste
  useEffect(() => {
    if (isOpen && activeTab === 'paste' && textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 100);
    }
  }, [isOpen, activeTab]);

  // Auto-detect delimiter when content changes
  useEffect(() => {
    if (activeContent) {
      const detected = detectDelimiter(activeContent);
      setDelimiter(detected);
    }
  }, [activeContent]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPasteContent('');
      setUploadedContent('');
      setUploadedFileName('');
      setError(null);
      setActiveTab('paste');
    }
  }, [isOpen]);

  const handlePasteChange = (e) => {
    setPasteContent(e.target.value);
    setError(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const content = await file.text();
      setUploadedContent(content);
      setUploadedFileName(file.name);
      setError(null);
    } catch (err) {
      setError(`Failed to read file: ${err.message}`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (activeTab === 'paste') {
      setPasteContent('');
    } else {
      setUploadedContent('');
      setUploadedFileName('');
    }
    setError(null);
  };

  const handleImport = async () => {
    if (!activeContent.trim()) {
      setError('Please paste or upload data to import');
      return;
    }

    if (!preview.hasAccountColumn) {
      setError("Data must include an 'Account' column");
      return;
    }

    if (preview.rowCount === 0) {
      setError('No data rows found in the content');
      return;
    }

    try {
      await onImport(activeContent, delimiter);
      onClose();
    } catch (err) {
      setError(err.message || 'Import failed. Please check your data format.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Upload size={20} className="text-orange-500" />
            <h2 className="text-xl font-semibold text-gray-800">Import Budget Data</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'paste'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('paste')}
          >
            <Clipboard size={16} />
            Paste Data
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'upload'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('upload')}
          >
            <FileText size={16} />
            Upload File
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'paste' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste your CSV or Excel data below
                </label>
                <textarea
                  ref={textareaRef}
                  value={pasteContent}
                  onChange={handlePasteChange}
                  placeholder="Paste data here...&#10;&#10;Expected format:&#10;Account,1/1/2025,2/1/2025,...,12/1/2025,Total&#10;1000-00,5000,5500,6000,...,5000,66000"
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload a CSV file
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-orange-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadedFileName ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle size={20} />
                      <span className="font-medium">{uploadedFileName}</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to select a file or drag and drop</p>
                      <p className="text-sm text-gray-400 mt-1">Supports .csv files</p>
                    </>
                  )}
                </div>
              </div>

              {uploadedContent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Preview
                  </label>
                  <div className="bg-gray-50 rounded-md p-3 max-h-48 overflow-auto">
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                      {uploadedContent.split('\n').slice(0, 10).join('\n')}
                      {uploadedContent.split('\n').length > 10 && '\n...'}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview Info */}
          {activeContent && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Data Preview</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Format:</span>{' '}
                  <span className="font-medium">{preview.format || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Columns:</span>{' '}
                  <span className="font-medium">{preview.columns.length}</span>
                </div>
                <div>
                  <span className="text-gray-500">Data Rows:</span>{' '}
                  <span className="font-medium">{preview.rowCount}</span>
                </div>
                <div>
                  <span className="text-gray-500">Account Column:</span>{' '}
                  <span className={`font-medium ${preview.hasAccountColumn ? 'text-green-600' : 'text-red-600'}`}>
                    {preview.hasAccountColumn ? 'Found' : 'Missing'}
                  </span>
                </div>
              </div>
              {preview.sampleAccounts.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm text-gray-500">Sample Accounts: </span>
                  <span className="text-sm font-mono text-gray-700">
                    {preview.sampleAccounts.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-4 text-xs text-gray-500">
            <p className="mb-1"><strong>Expected format:</strong></p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>First column must be "Account" with account numbers</li>
              <li>Date columns in M/D/YYYY format (e.g., 1/1/{selectedYear})</li>
              <li>Supports both comma-separated (CSV) and tab-separated (Excel copy) formats</li>
              <li>Account numbers must match your Chart of Accounts</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
            disabled={!activeContent}
          >
            Clear
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={isUploading || !activeContent}
              className="px-4 py-2 bg-orange-500 rounded-md text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetImportModal;
